import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Eye, EyeOff, User, Lock, ArrowRight, Loader2, Check } from 'lucide-react';
import AuthService from '../services/AuthService';

interface LoginProps {
  onLoginSuccess: (token: string) => void;
}

const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
  25% {
    transform: translateY(-20px) rotate(5deg) scale(1.05);
  }
  50% {
    transform: translateY(-10px) rotate(-5deg) scale(1);
  }
  75% {
    transform: translateY(-30px) rotate(3deg) scale(1.02);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientMove = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
`;

const checkBounce = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FFCC00 0%, #FFD700 50%, #FFCC00 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden;
  position: relative;
`;

const BgShape = styled.div<{ 
  delay: number; 
  size: number; 
  top?: string; 
  left?: string; 
  bottom?: string; 
  right?: string; 
}>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: ${float} 8s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  ${props => props.top && `top: ${props.top};`}
  ${props => props.left && `left: ${props.left};`}
  ${props => props.bottom && `bottom: ${props.bottom};`}
  ${props => props.right && `right: ${props.right};`}
`;

const LoginCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 10;
  animation: ${slideUp} 0.8s ease-out;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #D40511, #FFCC00, #D40511);
    background-size: 200% 100%;
    animation: ${gradientMove} 3s ease-in-out infinite;
  }

  @media (max-width: 480px) {
    margin: 20px;
    padding: 40px 25px;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;

  h1 {
    color: #D40511;
    font-size: 2.2rem;
    font-weight: 700;
    letter-spacing: 2px;
    animation: ${pulse} 2s ease-in-out infinite;
    margin: 0;

    @media (max-width: 480px) {
      font-size: 1.8rem;
    }
  }
`;

const DHLLogo = styled.img`
  height: 80px;
  width: auto;
  object-fit: contain;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const Subtitle = styled.p`
  color: #D40511;
  margin: 10px 0 20px 0;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.9;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #D40511 0%, #B22222 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SkyLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const SkyLogo = styled.img`
  height: 80px;
  width: auto;
  object-fit: contain;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
  position: relative;

  &:focus-within label {
    transform: translateX(5px);
  }

  &:focus-within label svg {
    transform: scale(1.2);
  }
`;

const InputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #D40511;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 10px;
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
    color: #D40511;
    transition: transform 0.3s ease;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fafafa;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #D40511;
    background: white;
    box-shadow: 0 0 0 4px rgba(212, 5, 17, 0.1);
  }

  &::placeholder {
    color: #aaa;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  cursor: pointer;
  color: #999;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  background: none;
  border: none;

  &:hover {
    color: #D40511;
    transform: scale(1.1);
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 30px;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  display: none;
`;

const CustomCheckbox = styled.span<{ checked: boolean }>`
  width: 22px;
  height: 22px;
  border: 2px solid #D40511;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: ${props => props.checked ? '#D40511' : 'white'};
  animation: ${props => props.checked ? checkBounce : 'none'} 0.4s ease;

  svg {
    width: 14px;
    height: 14px;
    color: white;
    opacity: ${props => props.checked ? 1 : 0};
    transform: scale(${props => props.checked ? 1 : 0});
    transition: all 0.3s ease;
  }
`;

const CheckboxLabel = styled.span`
  color: #D40511;
  font-size: 0.9rem;
  font-weight: 500;
  user-select: none;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #D40511 0%, #ff1a1a 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(212, 5, 17, 0.4);
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:hover:not(:disabled) svg:last-child {
    transform: translateX(5px);
  }

  &:active:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 22px;
    height: 22px;
    transition: transform 0.3s ease;
  }
`;

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const ErrorMessage = styled.div`
  background: rgba(212, 5, 17, 0.1);
  color: #D40511;
  padding: 15px;
  border-radius: 10px;
  border-left: 4px solid #D40511;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 20px;
  animation: ${slideUp} 0.3s ease-out;
`;

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = await AuthService.login(username, password);
      if (token) {
        onLoginSuccess(token);
      } else {
        setError('Credenciales inválidas. Verifica tu usuario y contraseña.');
      }
    } catch (err) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <BgShape delay={0} size={300} top="-100px" left="-100px" />
      <BgShape delay={2} size={200} bottom="-50px" right="-50px" />
      <BgShape delay={4} size={150} top="50%" right="10%" />
      <BgShape delay={1} size={100} bottom="20%" left="5%" />
      
      <LoginCard>
        <LoginHeader>
          <TitleContainer>
            <h1>SkyBot</h1>
            <DHLLogo src="/dhllogo.png" alt="DHL Logo" />
          </TitleContainer>
          <Subtitle>Asistente Virtual Inteligente</Subtitle>
          <SkyLogoContainer>
            <SkyLogo src="/skylogo.png" alt="Sky Logo" />
          </SkyLogoContainer>
        </LoginHeader>

        <LoginForm onSubmit={handleSubmit}>
          <FormGroup>
            <InputLabel>
              <User />
              Usuario
            </InputLabel>
            <InputWrapper>
              <Input
                type="text"
                placeholder="Ingresa tu usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputLabel>
              <Lock />
              Contraseña
            </InputLabel>
            <InputWrapper>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <LoginButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <SpinningLoader />
                Iniciando Sesión...
              </>
            ) : (
              <>
                Iniciar Sesión
                <ArrowRight />
              </>
            )}
          </LoginButton>
        </LoginForm>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
